import type { ReactNode } from 'react';
import { useId } from 'react';
import { Loader2 } from 'lucide-react';

import { GateSelectField } from '~/modules-rocco/enquiry/component/GateSelectField';
import {
    GateFieldError,
    GateFieldLabel,
    GateInput,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { SUBSCRIBER_TYPE_OPTIONS } from '../subscriber-type';
import {
    SubscribeLocationField,
    type SubscribeLocation,
} from './SubscribeLocationField';

export interface SubscribeFormValues {
    name: string;
    subscriberType: string;
    /** What the reader sees in the location box. */
    locationLabel: string;
    /** The resolved parts, present only once a suggestion has been picked. */
    location: SubscribeLocation | null;
    email: string;
}

interface SubscribeFormProps {
    values: SubscribeFormValues;
    onChange: (values: SubscribeFormValues) => void;
    onSubmit: () => void;
    onCancel: () => void;
    submitting: boolean;
    /** Server-side message, already routed to the field it belongs against. */
    error?: { message: string; field?: string };
    /**
     * Rendered between the heading and the fields. The mobile sheet has no side
     * pane, so the selection goes here — after the heading that explains it,
     * rather than above it where the reader meets a list of covers first.
     */
    selectionSlot?: ReactNode;
}

/**
 * The subscribe modal's right panel: who is subscribing, and the button.
 *
 * The publications are shown in the left pane, so this collects only the reader.
 * Every field is required: the subscribe is a submission of marketing's HubSpot
 * form, which rejects anything missing a name, an "I am a" answer or a region.
 *
 * The fields are the shared gate controls, so this reads as the same form as the
 * auth step it leads into — including the 16px floor that stops iOS zooming the
 * page and dragging the sheet with it.
 *
 * The form opts out of HubSpot's tracking script collecting it. The server
 * submits the real form, and a collected copy would land as a second submission
 * with the whole name in First name.
 */
export const SubscribeForm = ({
    values,
    onChange,
    onSubmit,
    onCancel,
    submitting,
    error,
    selectionSlot,
}: SubscribeFormProps) => {
    const nameId = useId();
    const locationId = useId();
    const emailId = useId();

    const set = (patch: Partial<SubscribeFormValues>) =>
        onChange({ ...values, ...patch });

    const errorFor = (field: string) =>
        error?.field === field ? error.message : undefined;
    const nameError = errorFor('Name');
    const typeError = errorFor('SubscriberType');
    const locationError = errorFor('Region');
    const emailError = errorFor('Email');
    const generalError = error && !error.field ? error.message : undefined;

    return (
        <form
            data-hs-do-not-collect="true"
            className="flex size-full flex-col"
            onSubmit={(event) => {
                event.preventDefault();
                onSubmit();
            }}
        >
            <h2 className="m-0 text-[2.0625rem] leading-[2.125rem] font-medium tracking-[-0.02em] text-[#181818]">
                Subscribe to ArchiPro Digital Publications
            </h2>

            {selectionSlot}

            <div className="mt-[1.1875rem] flex flex-col gap-[1.3125rem]">
                <div className="flex flex-col gap-1.5">
                    <GateFieldLabel htmlFor={nameId}>Your name</GateFieldLabel>
                    <GateInput
                        id={nameId}
                        name="full_name"
                        type="text"
                        required
                        autoComplete="name"
                        placeholder="First and last name"
                        editHint
                        value={values.name}
                        aria-invalid={nameError ? true : undefined}
                        onChange={(event) => set({ name: event.target.value })}
                    />
                    <GateFieldError message={nameError} />
                </div>

                {/* Not a native select: inside the phone sheet Chrome opened
                    the native picker well above the field, over the
                    publications. The portalled list is placed against the
                    trigger itself, so it opens under the field wherever the
                    sheet has moved to. */}
                <GateSelectField
                    label="I am a"
                    placeholder="Please select"
                    name="subscriber_type"
                    required
                    value={values.subscriberType}
                    options={SUBSCRIBER_TYPE_OPTIONS}
                    error={typeError}
                    onChange={(subscriberType) => set({ subscriberType })}
                />

                <SubscribeLocationField
                    id={locationId}
                    name="location"
                    value={values.locationLabel}
                    error={locationError}
                    onChange={(locationLabel, location) =>
                        set({ locationLabel, location })
                    }
                />

                <div className="flex flex-col gap-1.5">
                    <GateFieldLabel htmlFor={emailId}>
                        Your email
                    </GateFieldLabel>
                    <GateInput
                        id={emailId}
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        placeholder="Email address"
                        editHint
                        value={values.email}
                        aria-invalid={emailError ? true : undefined}
                        onChange={(event) => set({ email: event.target.value })}
                    />
                    <GateFieldError message={emailError} />
                </div>
            </div>

            {generalError ? (
                <p
                    className="m-0 mt-4 text-[0.8125rem] text-destructive"
                    role="alert"
                >
                    {generalError}
                </p>
            ) : null}

            <button
                type="submit"
                disabled={submitting}
                className="mt-auto flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:opacity-60"
            >
                {submitting ? (
                    <Loader2
                        className="size-5 animate-spin"
                        aria-hidden="true"
                    />
                ) : (
                    'Subscribe'
                )}
            </button>
            <button
                type="button"
                onClick={onCancel}
                className="mt-5 text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
            >
                Cancel
            </button>
        </form>
    );
};
